import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import BaseTable from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useClient } from "#/hook/useClient";
import { usePermission } from "#/hooks/usePermission";
import { exportToCSV } from "#/utils/CsvUtils";
import { logger } from "#/utils/logger";
import { useTableColumns } from "#/hooks/useTableColumns";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import type { Site } from "#/types/sites";
import { Button, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CreateSiteModal from "./components/CreateSiteModal";
import DeleteSiteModal from "./components/DeleteSiteModal";

const SitesPage = () => {
  const navigate = useNavigate();
  const { canWrite, canDelete } = usePermission();
  const { t } = useTranslation();
  const { client } = useClient();
  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | undefined>(undefined);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection] = useState<"ASC" | "DESC">("DESC");
  const [sortBy] = useState<string>("createdAt");

  // Columns come from the shared config filtered by the client's Column
  // Visibility setting; the Actions column is appended by the page.
  const { visible, headers: columnHeaders } = useTableColumns<"sites", Site>("sites", {
    name: {
      cellClassName: "capitalize min-w-[180px]",
      cell: (item) => item.name ?? "--",
    },
    location_text: {
      cellClassName: "min-w-[250px]",
      cell: (item) => item.location_text ?? "--",
    },
    updated_at: {
      cellClassName: "min-w-[200px]",
      cell: (item) => (
        <div className="w-full flex flex-col ">
          <span>
            {formatDate(item.updatedAt, client)}
            <br />
            {formatTime(item.updatedAt, client)}
          </span>
          {item.updated_by && (
            <div>
              {"by"}
              <b> {item.updated_by.name ?? item.updated_by.email}</b>
            </div>
          )}
        </div>
      ),
    },
  });

  const prepareExportData = (sitesToExport: typeof items) => {
    return sitesToExport.map((item) => ({
      id: item.id || "",
      name: item.name || "",
      location_text: item.location_text || "",
      latitude: item.latitude || "",
      longitude: item.longitude || "",
      updated_at: formatDateTimeForExport(item.updatedAt),
      updated_by: item.updated_by?.name || item.updated_by?.email || "",
    }));
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const exportHeaders = [
      "id",
      "name",
      "location_text",
      "latitude",
      "longitude",
      "updated_at",
      "updated_by",
    ];

    const headerLabels = {
      id: "ID",
      name: "Name",
      location_text: "Location",
      latitude: "Latitude",
      longitude: "Longitude",
      updated_at: "Last Updated",
      updated_by: "Updated By",
    };

    const note = t("common.utcNote");
    const filename = `sites-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      const response = await sitesApi.getAll({
        limit: total,
        page: 1,
        ...(sortBy ? { sortBy: sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
      });

      const allSites = response.data.data;
      const exportData = prepareExportData(allSites);

      const exportHeaders = [
        "id",
        "name",
        "location_text",
        "latitude",
        "longitude",
        "updated_at",
        "updated_by",
      ];

      const headerLabels = {
        id: "ID",
        name: "Name",
        location_text: "Location",
        latitude: "Latitude",
        longitude: "Longitude",
        updated_at: "Last Updated",
        updated_by: "Updated By",
      };

      const note = t("common.utcNote");
      const filename = `sites-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
    } catch (error) {
      logger.error("Failed to export all sites:", error);
    }
  };

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getSites,
  } = useQuery({
    queryKey: ["items", sortBy, page, sortBy, sortDirection],
    queryFn: async () => {
      try {
        const response = await sitesApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch {
        throw new Error("No items returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("sites.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          {/* <Popover
						anchor={
							<Button
								leftSection={<IconFilter size={18} />}
								variant={"outline"}
								color="black"
								size="sm"
								loading={isLoading}
							>
								Filters
							</Button>
						}
					>
						<div>sup</div>
					</Popover> */}
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                openCreateModal();
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("sites.createNewSite")}
            </Button>
          </PermissionTooltip>
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
            size="sm"
          >
            {t("common.exportCsv")}
          </Button>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...columnHeaders, t("common.actions")]}
          loading={isPending}
          allowSorting={false}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(item)}
                  </Table.Td>
                ))}
                <Table.Td style={{ minWidth: '140px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/sites/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        disabled={!canDelete()}
                        onClick={() => {
                          setSiteToDelete(item);
                          openDeleteModal();
                        }}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <DeleteSiteModal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setSiteToDelete(undefined);
        }}
        site={siteToDelete}
        onSuccess={() => {
          void getSites();
          setSiteToDelete(undefined);
        }}
      />

      <CreateSiteModal
        opened={createModalOpened}
        onClose={() => {
          closeCreateModal();
        }}
        onSuccess={() => {
          setPage(1);
          void getSites();
          closeCreateModal();
        }}
      />

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default SitesPage;
